import Link from 'next/link';
import { CtaRow, LivePill } from './blocks';

/**
 * Shared product-detail hero shell: breadcrumb, Six Caps H1 (max 2 lines),
 * lede of 20 words or fewer, CTA row (single "Book a demo" intent),
 * live-product pill. The visual is passed in per product (browser shot,
 * phone fan, etc.). All copy and links are props with English defaults so
 * the translated pages (ar, ckb) reuse the shell.
 */
export function ProductHero({
  name,
  accent,
  title,
  lede,
  secondary,
  liveHref,
  liveLabel,
  note,
  children,
  breadcrumbLabel = 'Products',
  productsHref = '/products',
  bookLabel = 'Book a demo',
  bookHref = '/contact',
  goLabel,
  openLabel,
}: {
  name: string;
  accent: string;
  title: string;
  lede: string;
  secondary: { label: string; href: string; external?: boolean };
  liveHref: string;
  liveLabel: React.ReactNode;
  /** Small mono addition next to the live pill (e.g. demo logins). */
  note?: React.ReactNode;
  children: React.ReactNode;
  breadcrumbLabel?: string;
  productsHref?: string;
  bookLabel?: string;
  bookHref?: string;
  goLabel?: string;
  openLabel?: string;
}) {
  return (
    <section className="relative flex min-h-[100dvh] flex-col justify-center overflow-hidden px-6 pb-16 pt-[clamp(7rem,12vw,9rem)] md:px-12 lg:px-24">
      <div className="mx-auto grid w-full max-w-[1400px] items-center gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <nav
            aria-label="Breadcrumb"
            className="pv-load font-mono text-[10px] uppercase tracking-[0.25em] rtl:tracking-normal text-white/40"
          >
            <Link href={productsHref} className="transition-colors duration-300 hover:text-white">
              {breadcrumbLabel}
            </Link>
            <span className="mx-3" aria-hidden="true">
              /
            </span>
            <span style={{ color: accent }}>{name}</span>
          </nav>
          <h1
            className="pv-load rtl-display-m mt-7 font-six-caps text-[clamp(3.8rem,7.5vw,7.5rem)] lowercase leading-[0.88] text-white"
            style={{ animationDelay: '100ms' }}
          >
            {title}
          </h1>
          <p
            className="pv-load mt-7 max-w-lg text-base font-light leading-relaxed text-white/65 md:text-lg"
            style={{ animationDelay: '220ms' }}
          >
            {lede}
          </p>
          <div className="pv-load" style={{ animationDelay: '340ms' }}>
            <CtaRow
              primary={{ label: bookLabel, href: bookHref }}
              secondary={secondary}
              accent={accent}
              className="mt-10"
              goLabel={goLabel}
              openLabel={openLabel}
            />
          </div>
          <div
            className="pv-load mt-7 flex flex-wrap items-center gap-x-5 gap-y-3"
            style={{ animationDelay: '460ms' }}
          >
            <LivePill href={liveHref} label={liveLabel} accent={accent} />
            {note ? (
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] rtl:tracking-normal text-white/40">
                {note}
              </span>
            ) : null}
          </div>
        </div>
        <div className="pv-load relative lg:col-span-7" style={{ animationDelay: '300ms' }}>
          {children}
        </div>
      </div>
    </section>
  );
}
